import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { fadeUp, fadeIn, stagger, viewport } from "@/lib/motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OFFERINGS = {
  "your-journey": {
    n: "01",
    title: "Your Journey",
    tagline: "1:1 Exploration",
    quote: "Greater coherence between the inner world, outer reality and the ripples we create.",
    intro:
      "For those seeking greater coherence between their inner world, outer reality and the ripples they create around them.",
    body: [
      "A personalised exploration of self, patterns, values and direction, supporting emotional, energetic and systemic alignment across life, relationships and work.",
      "We move between reflection and structure — listening to what is asking to emerge, then shaping the conditions, language and rhythm that allow it to live.",
      "The process begins with an alignment call.",
    ],
    img: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=2000&q=80",
    primary: { kind: "calendly", label: "Book an alignment call" },
    intent: "journey",
  },
  "vision-architecture": {
    n: "02",
    title: "Vision Architecture",
    tagline: "For visions still becoming",
    quote: "For those carrying something they cannot yet fully describe, but know wants to emerge.",
    intro:
      "Supporting people with ideas, projects and emerging visions to translate intuition into structure.",
    body: [
      "We shape offerings, frameworks, direction and next steps without losing the soul of the work.",
      "For those building something in the world and needing clarity, reflection and systems that support expansion. We work energetically, creatively, strategically, and systematically.",
      "The work begins with a conversation, listening for what is true beneath the noise.",
    ],
    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2000&q=80",
    primary: { kind: "form", label: "Request a conversation" },
    intent: "collaboration",
  },
  "collective-experiences": {
    n: "03",
    title: "Collective Experiences",
    tagline: "Organisations, teams & communities",
    quote: "Stronger cultures, deeper connection, greater harmony.",
    intro:
      "Curating workshops, retreats and facilitated experiences designed around the unique needs, tensions and questions present within a group.",
    body: [
      "We support stronger cultures, deeper connection and greater harmony between people, values and the systems they operate within.",
      "Together we may explore belonging, communication, identity, collective care and what allows people to move with greater clarity around a shared vision — strengthening trust, participation and the unseen structures shaping communities.",
      "Every gathering is co-designed, never templated.",
    ],
    img: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/gd8dsrqm_IMG_1570.jpeg",
    primary: { kind: "form", label: "Bring a gathering in" },
    intent: "workshop",
  },
  "framework-story-development": {
    n: "04",
    title: "Framework & Story Development",
    tagline: "Translating ideas into form",
    quote: "Built to be lived with, not just read.",
    intro:
      "Working with thinkers and organisations to translate complex ideas into frameworks, narratives and tools that travel.",
    body: [
      "Built to be lived with, not just read — frameworks that hold meaning over time and stories that carry the soul of the work into the world.",
      "Whether it's a methodology being shaped, a brand finding its voice, or a movement looking for clearer language, we work together to give form to what already exists in essence.",
    ],
    img: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/6i2tdaxi_IMG_1564.JPG",
    primary: { kind: "form", label: "Shape it together" },
    intent: "collaboration",
  },
  "speaking-collaborations": {
    n: "05",
    title: "Speaking & Collaborations",
    tagline: "Shared inquiries",
    quote: "Spaces exploring how we live, lead, relate and build together.",
    intro:
      "Through reflections, stories and evolving frameworks, I support conversations around harmony, belonging and the relationship between inner worlds and the systems surrounding us.",
    body: [
      "Open to talks, collaborations, residencies and co-created experiences.",
      "I bring an architect's eye to systemic questions and a facilitator's heart to the rooms we share. The conversations that move me most live at the edges — where disciplines meet, where wounds are honest, where new possibilities are still forming.",
    ],
    img: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/smjnan05_IMG_1545.JPG",
    primary: { kind: "form", label: "Invite a conversation" },
    intent: "speaking",
  },
};

export default function OfferingPage() {
  const { slug } = useParams();
  const item = OFFERINGS[slug];

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!item) {
    return (
      <div className="bg-bone min-h-screen">
        <Nav />
        <div className="py-40 text-center">
          <p className="font-serif italic text-3xl text-charcoal">Offering not found.</p>
          <Link
            to="/#offerings"
            className="mt-6 inline-block text-bronze link-underline text-sm uppercase tracking-[0.22em]"
          >
            Back to offerings
          </Link>
        </div>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        intent: item.intent,
        message: `[Offering: ${item.title}]\n\n${form.message || "(no message)"}`,
        preferred_time: "",
      });
      setDone(true);
      toast.success("Received. I'll respond personally, with care.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something didn't go through. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const isCalendlyPrimary = item.primary.kind === "calendly";

  return (
    <div className="bg-bone min-h-screen text-charcoal">
      <Nav />

      {/* HERO — arched portrait + editorial title */}
      <header
        className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
        data-testid="offering-page"
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-12">
          <Link
            to="/#offerings"
            data-testid="offering-back"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-stone hover:text-bronze transition-colors duration-500"
          >
            <ArrowLeft size={14} /> Back to offerings
          </Link>

          <div className="grid md:grid-cols-12 gap-10 md:gap-16 mt-14 items-start">
            {/* Left — text */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger(0.12)}
              className="md:col-span-7"
            >
              <motion.p variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-bronze">
                ✦ &nbsp; Offering {item.n} &nbsp; ✦
              </motion.p>
              <motion.h1
                variants={fadeUp}
                data-testid="offering-title"
                className="mt-6 font-serif text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight text-charcoal"
              >
                {item.title.split(" & ").map((s, i, arr) => (
                  <span key={i} className={i === 1 ? "italic text-bronze block" : "block"}>
                    {s}
                    {i < arr.length - 1 && " &"}
                  </span>
                ))}
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mt-5 text-[11px] uppercase tracking-[0.22em] text-stone"
              >
                {item.tagline}
              </motion.p>
              <motion.p
                variants={fadeUp}
                className="mt-10 font-serif italic text-2xl md:text-3xl text-bronze leading-snug"
              >
                &ldquo;{item.quote}&rdquo;
              </motion.p>
              <motion.p
                variants={fadeUp}
                className="mt-8 text-lg text-ink leading-relaxed font-light max-w-xl"
              >
                {item.intro}
              </motion.p>
            </motion.div>

            {/* Right — arched image */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="md:col-span-5"
            >
              <div
                className="zoom-container relative aspect-[3/4] overflow-hidden bg-clay"
                style={{ borderTopLeftRadius: "260px", borderTopRightRadius: "260px" }}
              >
                <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/10 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* BODY */}
      <section className="bg-sand py-20 md:py-28">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={stagger(0.14)}
            className="space-y-7 text-ink text-lg leading-relaxed font-light"
          >
            {item.body.map((p, i) => (
              <motion.p key={i} variants={fadeUp}>
                {p}
              </motion.p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA / REQUEST */}
      <section className="bg-bone py-24 md:py-32" data-testid="offering-cta-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6">
            ✦ &nbsp; Begin &nbsp; ✦
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {isCalendlyPrimary ? (
              <>
                Ready to <span className="italic text-bronze">align</span>?
              </>
            ) : (
              <>
                Let&apos;s <span className="italic text-bronze">explore together</span>.
              </>
            )}
          </h2>

          {isCalendlyPrimary ? (
            <>
              <p className="mt-6 text-ink leading-relaxed font-light">
                Every journey opens with an alignment call. A space to meet,
                listen, and feel into whether this is the right time and way to
                walk together.
              </p>
              <a
                href="https://calendly.com/harmoniaportal-5"
                target="_blank"
                rel="noreferrer"
                data-testid="offering-calendly"
                className="btn-pill mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-charcoal text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-forest"
              >
                <Calendar size={14} /> {item.primary.label}
              </a>
              <p className="mt-12 text-[10px] uppercase tracking-[0.3em] text-stone">
                Or — write a few words instead
              </p>
            </>
          ) : (
            <p className="mt-6 text-ink leading-relaxed font-light">
              Share a few words about what&apos;s alive for you, and I&apos;ll
              respond personally to see how we might shape this together.
            </p>
          )}

          {/* Request form (always present) */}
          <form onSubmit={onSubmit} className="mt-10 space-y-7 text-left">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                  Your name
                </label>
                <input
                  data-testid="offering-name"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Who is writing"
                  className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                  Email
                </label>
                <input
                  data-testid="offering-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@somewhere.com"
                  className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                What&apos;s alive for you?{" "}
                <span className="lowercase text-stone/60">(optional)</span>
              </label>
              <textarea
                data-testid="offering-message"
                value={form.message}
                onChange={onChange("message")}
                rows={5}
                placeholder="A few words about what draws you here…"
                className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors resize-none"
              />
            </div>
            <div className="pt-2 text-center">
              <button
                type="submit"
                data-testid="offering-submit"
                disabled={submitting || done}
                className={`btn-pill group inline-flex items-center gap-3 px-7 py-3.5 rounded-full text-[11px] uppercase tracking-[0.22em] disabled:opacity-60 ${
                  isCalendlyPrimary
                    ? "border border-charcoal/40 text-charcoal hover:bg-charcoal hover:text-bone"
                    : "bg-charcoal text-bone hover:bg-forest"
                }`}
              >
                {done
                  ? "Received, thank you"
                  : submitting
                  ? "Sending…"
                  : isCalendlyPrimary
                  ? "Send a message instead"
                  : item.primary.label}
                {!done && (
                  <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                )}
              </button>
            </div>
          </form>

          {/* Quiet footer line */}
          <p className="mt-16 font-serif italic text-bronze text-xl md:text-2xl leading-snug">
            Each door is open. Walk through the one that calls you.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
